import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const isUIPreview = process.env.REACT_APP_UI_PREVIEW === 'true';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(isUIPreview ? { phone: '13800138000', isAdmin: 0 } : null);
  const [isLoading, setIsLoading] = useState(!isUIPreview);
  const [isAuthenticated, setIsAuthenticated] = useState(!!isUIPreview);

  // Check if user is already logged in on app start（预览模式下跳过鉴权）
  useEffect(() => {
    if (isUIPreview) {
      setIsLoading(false);
      return;
    }
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // Add a timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      // Use the new token verification endpoint
      const response = await fetch('/api/auth/verify-token', {
        method: 'GET',
        credentials: 'include',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        if (data.code === 0 && data.data.valid) {
          // User is authenticated, use phone from response or fallback to cookie
          const userPhone = data.data.phone || getCookie('user_phone') || 'Unknown';
          const isAdmin = data.data.isAdmin || 0;
          setUser({ phone: userPhone, isAdmin });
          setIsAuthenticated(true);
        } else {
          clearAuth();
        }
      } else {
        clearAuth();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // If the request was aborted (timeout), or if there's a network error,
      // assume the backend is not available and clear auth
      if (error.name === 'AbortError') {
        console.log('Auth check timed out - backend may not be running');
      }
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials) => {
    setIsLoading(true);
    try {
      const { loginPaaS } = await import('../services/auth');
      const result = await loginPaaS(credentials);
      
      if (result.ok) {
        // Set user info
        setUser({ phone: credentials.phone, isAdmin: 0 });
        setIsAuthenticated(true);
        return { success: true };
      } else {
        return { 
          success: false, 
          error: result.msg || 'Login failed',
          code: result.code 
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: 'Network error, please try again' 
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Call logout API
      await fetch('/api/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Clear local state regardless of API call result
      clearAuth();
    }
  };

  const clearAuth = () => {
    setUser(null);
    setIsAuthenticated(false);
    // Clear all user-specific localStorage data
    localStorage.removeItem('user_data');
    localStorage.removeItem('selectedAvatar');
    localStorage.removeItem('selectedVoice');
  };

  // Helper function to get cookie value
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};